import React, { useMemo, useState } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
// Generate random transactions
const generateTransactions = (count: number) => {
  const schemes = [{
    name: 'Visa',
    logo: 'https://cdn.magicpatterns.com/uploads/uyg5nASZDGjrCe2rJaNmzm/Visa_Logo.png',
    color: 'text-blue-600'
  }, {
    name: 'MasterCard',
    logo: 'https://cdn.magicpatterns.com/uploads/wTaFUzAaMXmqM9o6YPo8t5/MasterCard_Logo.png',
    color: 'text-green-600'
  }, {
    name: 'UnionPay',
    logo: 'https://cdn.magicpatterns.com/uploads/8qxbwmnN4pa5FLphCCtDLR/UnionPay_Logo.png',
    color: 'text-red-600'
  }, {
    name: 'PayPak',
    logo: 'https://cdn.magicpatterns.com/uploads/vGk29PVKWf7oAsHTkLwRwU/PayPak_Logo.png',
    color: 'text-green-600'
  }, {
    name: 'RAAST',
    logo: 'https://cdn.magicpatterns.com/uploads/dW1dUF5BFKkWuGeGttfoz6/RAAST_Logo.png',
    color: 'text-purple-600'
  }];
  const paymentModes = ['POS', 'Mobile POS', 'Soft POS', 'Online', 'RAAST'];
  const statuses = [{
    name: 'Completed',
    class: 'bg-green-100 text-green-800'
  }, {
    name: 'Pending',
    class: 'bg-yellow-100 text-yellow-800'
  }, {
    name: 'Failed',
    class: 'bg-red-100 text-red-800'
  }];
  const transactions = [];
  for (let i = 0; i < count; i++) {
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30));
    const scheme = schemes[Math.floor(Math.random() * schemes.length)];
    const paymentMode = paymentModes[Math.floor(Math.random() * paymentModes.length)];
    let transactionType = '';
    if (paymentMode === 'RAAST') {
      const types = ['Purchase', 'QRC', 'RTP'];
      transactionType = types[Math.floor(Math.random() * types.length)];
    } else {
      const types = ['Purchase', 'E-Commerce'];
      transactionType = types[Math.floor(Math.random() * types.length)];
    }
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const amount = (Math.random() * 1000).toFixed(2);
    transactions.push({
      id: `TX${Math.floor(Math.random() * 1000000000)}`,
      date: date.toLocaleString(),
      scheme,
      paymentMode,
      transactionType,
      amount,
      status
    });
  }
  return transactions;
};
const TransactionHistoryPage = () => {
  // Use useMemo to ensure transactions are only generated once
  const allTransactions = useMemo(() => generateTransactions(50), []);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScheme, setFilterScheme] = useState('');
  const [filterPaymentMode, setFilterPaymentMode] = useState('');
  const [filterTransactionType, setFilterTransactionType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const transactionsPerPage = 10;
  // Filter transactions
  const filteredTransactions = allTransactions.filter(transaction => {
    const matchesSearch = transaction.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesScheme = !filterScheme || transaction.scheme.name === filterScheme;
    const matchesMode = !filterPaymentMode || transaction.paymentMode === filterPaymentMode;
    const matchesType = !filterTransactionType || transaction.transactionType === filterTransactionType;
    const matchesStatus = !filterStatus || transaction.status.name === filterStatus;
    return matchesSearch && matchesScheme && matchesMode && matchesType && matchesStatus;
  });
  // Pagination
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);
  const paginate = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };
  const openDetails = (transaction: any) => {
    // Create a deep copy to ensure the modal shows exact data
    setSelectedTransaction({
      ...transaction,
      scheme: {
        ...transaction.scheme
      },
      status: {
        ...transaction.status
      }
    });
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
  };
  return <DashboardLayout data-id="element-2096">
      <div className="mb-6" data-id="element-2097">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2098">
          Transaction History
        </h1>
        <p className="text-gray-600" data-id="element-2099">
          View and search your transaction records
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-2100">
        <div className="flex flex-wrap gap-4 mb-6" data-id="element-2101">
          <div className="flex-1 min-w-[200px]" data-id="element-2102">
            <div className="relative" data-id="element-2103">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2104">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2105" />
              </div>
              <input type="text" placeholder="Search by Transaction ID" value={searchTerm} onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2106" />
            </div>
          </div>

          <div data-id="element-2107">
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={filterScheme} onChange={e => {
            setFilterScheme(e.target.value);
            setCurrentPage(1);
          }} data-id="element-2108">
              <option value="" data-id="element-2109">All Schemes</option>
              <option value="Visa" data-id="element-2110">Visa</option>
              <option value="MasterCard" data-id="element-2111">MasterCard</option>
              <option value="UnionPay" data-id="element-2112">UnionPay</option>
              <option value="PayPak" data-id="element-2113">PayPak</option>
              <option value="RAAST" data-id="element-2114">RAAST</option>
            </select>
          </div>

          <div data-id="element-2115">
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={filterPaymentMode} onChange={e => {
            setFilterPaymentMode(e.target.value);
            setCurrentPage(1);
          }} data-id="element-2116">
              <option value="" data-id="element-2117">All Payment Modes</option>
              <option value="POS" data-id="element-2118">POS</option>
              <option value="Mobile POS" data-id="element-2119">Mobile POS</option>
              <option value="Soft POS" data-id="element-2120">Soft POS</option>
              <option value="Online" data-id="element-2121">Online</option>
              <option value="RAAST" data-id="element-2122">RAAST</option>
            </select>
          </div>

          <div data-id="element-2123">
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={filterTransactionType} onChange={e => {
            setFilterTransactionType(e.target.value);
            setCurrentPage(1);
          }} data-id="element-2124">
              <option value="" data-id="element-2125">All Transaction Types</option>
              <option value="Purchase" data-id="element-2126">Purchase</option>
              <option value="QRC" data-id="element-2127">QRC</option>
              <option value="E-Commerce" data-id="element-2128">E-Commerce</option>
              <option value="RTP" data-id="element-2129">RTP</option>
            </select>
          </div>

          <div data-id="element-2130">
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={filterStatus} onChange={e => {
            setFilterStatus(e.target.value);
            setCurrentPage(1);
          }} data-id="element-2131">
              <option value="" data-id="element-2132">All Statuses</option>
              <option value="Completed" data-id="element-2133">Completed</option>
              <option value="Pending" data-id="element-2134">Pending</option>
              <option value="Failed" data-id="element-2135">Failed</option>
            </select>
          </div>

          <div data-id="element-2136">
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2137">
              <FilterIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-2138" />
              More Filters
            </button>
          </div>

          <div data-id="element-2139">
            <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-2140">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2141" />
              Export
            </button>
          </div>
        </div>

        {/* Transaction Table */}
        <div className="overflow-x-auto" data-id="element-2142">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2143">
            <thead data-id="element-2144">
              <tr data-id="element-2145">
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2146">
                  Transaction ID
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2147">
                  Date & Time
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2148">
                  Scheme
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2149">
                  Payment Mode
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2150">
                  Transaction Type
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2151">
                  Amount
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2152">
                  Status
                </th>
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2153">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2154">
              {currentTransactions.map(transaction => <tr key={transaction.id} data-id="element-2155">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2156">
                    {transaction.id}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2157">
                    {transaction.date}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2158">
                    <div className="flex items-center" data-id="element-2159">
                      <img src={transaction.scheme.logo} alt={transaction.scheme.name} className="h-5 w-auto object-contain mr-2" data-id="element-2160" />
                      {transaction.scheme.name}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2161">
                    {transaction.paymentMode}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2162">
                    {transaction.transactionType}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2163">
                    ₨{transaction.amount}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" data-id="element-2164">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.status.class}`} data-id="element-2165">
                      {transaction.status.name}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2166">
                    <div className="flex items-center space-x-3" data-id="element-2167">
                      <button onClick={() => openDetails(transaction)} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2168">
                        <EyeIcon className="h-4 w-4 mr-1" data-id="element-2169" />
                        View
                      </button>
                      {transaction.status.name === 'Completed' && <button className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2170">
                          <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-2171" />
                          Refund
                        </button>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between" data-id="element-2172">
          <div className="text-sm text-gray-500" data-id="element-2173">
            Showing {indexOfFirstTransaction + 1} to{' '}
            {Math.min(indexOfLastTransaction, filteredTransactions.length)} of{' '}
            {filteredTransactions.length} transactions
          </div>
          <div className="flex space-x-2" data-id="element-2174">
            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className={`flex items-center px-3 py-1 border border-gray-300 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2175">
              <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-2176" />
              Previous
            </button>
            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className={`flex items-center px-3 py-1 border border-gray-300 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2177">
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2178" />
            </button>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {showModal && selectedTransaction && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2179">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-2180">
            <div className="flex items-center justify-between p-6 border-b" data-id="element-2181">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-2182">
                Transaction Details
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" data-id="element-2183">
                <XIcon className="h-6 w-6" data-id="element-2184" />
              </button>
            </div>

            <div className="p-6 space-y-4" data-id="element-2185">
              <div className="grid grid-cols-2 gap-4" data-id="element-2186">
                <div data-id="element-2187">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2188">
                    Transaction ID
                  </h3>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2189">
                    {selectedTransaction.id}
                  </p>
                </div>
                <div data-id="element-2190">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2191">
                    Date & Time
                  </h3>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2192">
                    {selectedTransaction.date}
                  </p>
                </div>
                <div data-id="element-2193">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2194">Scheme</h3>
                  <div className="mt-1 flex items-center text-sm text-gray-900" data-id="element-2195">
                    <img src={selectedTransaction.scheme.logo} alt={selectedTransaction.scheme.name} className="h-5 w-auto object-contain mr-2" data-id="element-2196" />
                    {selectedTransaction.scheme.name}
                  </div>
                </div>
                <div data-id="element-2197">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2198">
                    Payment Mode
                  </h3>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2199">
                    {selectedTransaction.paymentMode}
                  </p>
                </div>
                <div data-id="element-2200">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2201">
                    Transaction Type
                  </h3>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2202">
                    {selectedTransaction.transactionType}
                  </p>
                </div>
                <div data-id="element-2203">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2204">Amount</h3>
                  <p className="mt-1 text-lg font-semibold text-gray-900" data-id="element-2205">
                    ₨{selectedTransaction.amount}
                  </p>
                </div>
                <div data-id="element-2206">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2207">Status</h3>
                  <div className="mt-1" data-id="element-2208">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedTransaction.status.class}`} data-id="element-2209">
                      {selectedTransaction.status.name}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t" data-id="element-2210">
              <button onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2211">
                Close
              </button>
              {selectedTransaction.status.name === 'Completed' && <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-2212">
                  Process Refund
                </button>}
            </div>
          </div>
        </div>}
    </DashboardLayout>;
};
export default TransactionHistoryPage;